import axios from 'axios'
import { apiUrl } from 'Services/accessUrl'
import jsonWebToken from 'Services/jsonWebToken'
import { ApiResult, ApiQuoteNegotiationDetail } from 'Types'

const endpoint = `${apiUrl.stable}/anunciante`

/**
 * Returns information about a specific negotiation
 * @param {number} quotationId The id of the desired quotation
 */
export const getNegotiation = (quotationId: number): ApiResult<ApiQuoteNegotiationDetail> => {
  const url = `${endpoint}/negociacao/detalhes/${quotationId}`

  const axiosConfig = {
    headers: {
      ...jsonWebToken.header,
    }
  }

  return axios.get(url, axiosConfig)
}

/**
 * Returns the history of actions and messages of a specific negotiation
 * @param {number} negotiationId The id of desired negotiation
 * @param {number} connectionId The SignalR connection id for realtime chat
 * @param {number} [page = 1] Desired page of results
 * @returns {Promise.<{ data: ApiChatHistory }>}
 */
export const getNegotiationHistory = (negotiationId, connectionId, page = 1) => {
  const url = `${endpoint}/historico`

  const params = {
    CompanyQuotationId: negotiationId,
    Page: page,
    ConnectionId: connectionId,
  }

  const headers = {
    ...jsonWebToken.header,
  }

  return axios.get(url, { params, headers })
}

/**
 * Returns the history previous messages based on a specific datetime
 * @param {string} dateISO A date string in ISO format (ISO 8601)
 * @param {number} [pageSize=10] How many messages should be retrieved
 * @returns {Promise.<{ data: ApiChatHistoryMessage[] }>}
 */
export const getHistoryPreviousMessages = (negotiationId, pageNumber, pageSize = 10) => {
  const url = `${endpoint}/historico/mensagens-anteriores`

  const params = {
    CompanyQuotationId: negotiationId,
    Page: pageNumber,
    PageSize: pageSize
  }

  const headers = {
    ...jsonWebToken.header
  }

  return axios.get(url, { params, headers })
}

/**
 * Returns a list of negotiations bought by the signed company
 * @param {number} [page=1] Desired page of results
 * @param {string} [filter=''] Term to filter the results
 */
export const getNegotiationList = (page = 1, query = {}) => {
  const url = `${endpoint}/negociacao`;

  const params = new URLSearchParams();
  params.append('Page', page);
  params.append('Sort', query.sort || null);
  params.append('StartDate', query.startDate || null);
  params.append('EndDate', query.endDate || null);

  if(query.filter !== '') {
    params.append('Filter', query.filter);
  }

  if (Array.isArray(query.status)) {
    query.status.forEach(status => params.append('Status', status));
  } else if (query.status !== undefined && query.status !== null) {
    params.append('status', query.status);
  }

  if (Array.isArray(query.activities)) {
    query.activities.forEach(activity => params.append('Activities', activity));
  } else if (query.activities !== undefined && query.activities !== null) {
    params.append('activities', query.activities);
  }

  if (Array.isArray(query.serviceType)) {
    query.serviceType.forEach(activity => params.append('ServiceTypes', activity));
  } else if (query.serviceType !== undefined && query.serviceType !== null) {
    params.append('ServiceTypes', query.serviceType);
  }

  const axiosConfig = {
    params: params,
    headers: {
      ...jsonWebToken.header,
    },
  };

  return axios.get(url, axiosConfig);
}


/**
 * Add an acction to the the history of a specific negotiation
 * @param {ApiChatNewMessage} data
 */
export const updateNegotiationHistory = (data) => {
  const url = `${endpoint}/historico`

  const axiosConfig = {
    headers: { ...jsonWebToken.header, }
  }

  return axios.post(url, data, axiosConfig)
}

/**
 * Update the status of a specific negotiation. This will not change the status for the other part.
 * @param {number} companyQuotationId The id of the desired quotation
 * @param {number} status The id of the desired status
 */
export const updateNegotiationStatus = (companyQuotationId, status) => {
  const url = `${endpoint}/alterar/tag`

  const axiosConfig = {
    headers: { ...jsonWebToken.header, }
  }

  const data = {
    companyQuotationId,
    status
  }

  return axios.put(url, data, axiosConfig)
}

/**
 * QuotationEvoluationController
 * @param {number} quotationId
 * avaliação do solicitante
 */
 export const getRequestRate = (negotiationId) => {
  const url = `${apiUrl.stable}/solicitante/avaliacoes/negociacao/${negotiationId}`

  const headers = {
    ...jsonWebToken.header
  }

  return axios.get(url, { headers })
}

export const getRequesterRateList = (params) => {
  const url = `${apiUrl.stable}/solicitante/avaliacoes`

  const headers = {
    ...jsonWebToken.header
  }

  return axios.get(url, { params, headers })
}

export const getSenderInfo = (idUser) => {
  const url = `${apiUrl.stable}/solicitante/avaliacoes/info/${idUser}`

  const headers = {
    ...jsonWebToken.header
  }

  return axios.get(url, { headers })
}

export const addRequesterRate = (data) => {
  const url = `${apiUrl.stable}/solicitante/avaliacoes/salvar`

  const headers = { ...jsonWebToken.header }

  return axios.post(url, data, { headers })
}

export const updateRequesterRate = (data) => {
  const url = `${apiUrl.stable}/solicitante/avaliacoes/atualizar`

  const headers = { ...jsonWebToken.header }

  return axios.put(url, data, { headers })
}

/**
 * Arquiva uma negociação da empresa
 * @param {number} quotationId
 * @param {number} companyQuotationId
 */
export const archiveNegotiation = (quotationId: number, companyQuotationId: number) => {
  const url = `${endpoint}/arquivar`

  const data = { 
    QuotationId: quotationId,
    CompanyQuotationId: companyQuotationId
  }

  const headers = { ...jsonWebToken.header }

  return axios.post(url, data, { headers })
}

export default {
  getNegotiation,
  getNegotiationHistory,
  getNegotiationList,
  updateNegotiationHistory,
  updateNegotiationStatus,
  getRequesterRateList,
  addRequesterRate,
  updateRequesterRate,
  archiveNegotiation,
}